import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";

const EASE = [0.76, 0, 0.24, 1];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ClientListModal({ open, onClose }) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const handleClose = () => {
    onClose();
    setEmail("");
    setDone(false);
    setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || !email) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(`${API}/client-list-request`, { email });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center px-6">
          <motion.div
            data-testid="client-list-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="absolute inset-0 bg-[#0a0a0b]/90 backdrop-blur-sm"
            onClick={handleClose}
          />
          <motion.div
            data-testid="client-list-modal"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.98 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="relative w-full max-w-md border border-white/15 bg-[#0a0a0b] p-8 md:p-10"
          >
            <button
              data-testid="client-list-modal-close"
              onClick={handleClose}
              aria-label="Close"
              className="absolute top-5 right-5 text-white/50 hover:text-white transition-colors mono-label"
            >
              close
            </button>

            {done ? (
              <div data-testid="client-list-modal-success">
                <span className="mono-label">Sent</span>
                <h3 className="font-display !normal-case text-3xl mt-5 leading-tight text-white">
                  On its way.
                </h3>
                <p className="text-white/60 mt-4 leading-relaxed">
                  Our client list is headed to <span className="text-white">{email}</span>.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="client-list-modal-form">
                <span className="mono-label">Client List</span>
                <h3 className="font-display !normal-case text-3xl mt-5 leading-tight text-white">
                  Available upon request.
                </h3>
                <p className="text-white/60 mt-4 leading-relaxed">
                  Enter your email and we&rsquo;ll send our full client roster directly to your inbox.
                </p>
                <div className="mt-8">
                  <label className="mono-label" htmlFor="client-list-email">Email</label>
                  <input
                    id="client-list-email"
                    data-testid="client-list-modal-email"
                    type="email"
                    required
                    className="hd-input"
                    placeholder="you@studio.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  {error && (
                    <p data-testid="client-list-modal-error" className="text-red-400 text-sm mt-2">
                      {error}
                    </p>
                  )}
                </div>
                <div className="pt-8 flex items-center justify-end border-t border-white/[0.08] mt-6">
                  <button
                    data-testid="client-list-modal-submit"
                    type="submit"
                    disabled={submitting}
                    className="group flex items-center gap-4 font-display !normal-case text-xl border-b border-white/40 hover:border-white pb-2 transition-colors disabled:opacity-40"
                  >
                    <span>{submitting ? "Sending…" : "Send it to me"}</span>
                    <motion.span
                      animate={submitting ? {} : { x: [0, 6, 0] }}
                      transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    >
                      →
                    </motion.span>
                  </button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
